import {useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import Alert from '../../common/component/Alert';
import {WEB_HOST} from '../../common/constant';
import {Page} from '../../common/constant/page';
import {PageAppContext} from '../../common/context';
import {getLoginReturnPageInfo} from '../../common/function';
import useEffectOnChange from '../../common/hook/useEffectOnChange';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {ButtonSet} from '../../common/type/alert';
import {getUrl, PageInfo, routeTo} from '../../common/util/PageRouter';
import {
  getAccessToken,
  getStoredApiParamsAuthResult,
  sendAuthEvent,
  sendAuthViewImp,
  startIdVerification,
} from '../common';
import IdVerificationModalDesc from '../component/IdVerificationModalDesc';
import useCheckTerms from '../hook/useCheckTerms';
import {actions} from '../state/action';
import {IIdVerificationModalType, ITermsItem} from '../type';

export default function IdVerificationModal() {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const isInSerivice = useContext(PageAppContext).page !== Page.AuthTerms;

  const [idVerificationModal, termsParentList, apiParamsAuthResult, page] =
    useTypedSelector(state => [
      state.auth.idVerificationModal,
      state.auth.termsParentList,
      getStoredApiParamsAuthResult(state),
      state.common.layoutProps.page,
    ]);

  const modalTextObj = useMemo(
    () =>
      idVerificationModal &&
      MODAL_TEXT_MAP[idVerificationModal.type](isInSerivice),
    [idVerificationModal, isInSerivice]
  );

  const [isShow, setIsShow] = useState(false);
  const [termsList, setTermsList] = useState<ITermsItem[]>([]);

  useEffect(() => {
    if (idVerificationModal && modalTextObj) {
      if (idVerificationModal.type === 'parent') {
        setTermsList(termsParentList);
      } else {
        const {agreementText, pageInfo} = modalTextObj;
        setTermsList([
          {
            id: 0,
            title: t(agreementText || ''),
            detailPath: getUrl(pageInfo),
            isRequired: true,
            version: '1', // 타입을 맞추기 위한 임의 값
          },
        ]);
      }
    }
  }, [idVerificationModal, modalTextObj, termsParentList, t]);

  const {termsCheckedObj, setTermsCheckedObj, isConfirmEnabled, getAccepted} =
    useCheckTerms({
      termsList,
    });

  const onChangeItem = (id: number) => {
    const newIsCheckedObj = {...termsCheckedObj, [id]: !termsCheckedObj[id]};
    setTermsCheckedObj(newIsCheckedObj);
  };

  useEffectOnChange(() => {
    setIsShow(
      !!idVerificationModal &&
        !(
          page === Page.MorePolicyLimit14 ||
          page === Page.MorePersonalInformationShort ||
          page === Page.MorePolicyPersonalInformation ||
          page === Page.MorePolicyLegalRepresentative ||
          page === Page.MorePolicyChildPersonalInformation ||
          page === Page.MorePolicyChildPersonalThirdParty ||
          page === Page.MorePolicyAdvertisement
        )
    );
  }, page);

  useEffect(() => {
    setIsShow(!!idVerificationModal);
  }, [idVerificationModal]);

  useEffect(() => {
    if (idVerificationModal?.type === 'parent') {
      dispatch(
        actions.fetchParentTermsList(apiParamsAuthResult.verificationSessionId)
      );
    }
  }, [dispatch, idVerificationModal, apiParamsAuthResult]);

  useEffect(() => {
    if (isShow && idVerificationModal) {
      sendAuthViewImp({
        customProps: {popup_type: POPUP_TYPE_MAP[idVerificationModal.type]},
        viewImpContents: {name: 'popup'},
      });
    }
  }, [isShow, idVerificationModal]);

  const onClickConfirm = () => {
    if (idVerificationModal) {
      const type = idVerificationModal.type;
      sendAuthEvent({
        actionName: type === 'parent' ? 'popup_verify_under14' : 'popup_verify',
        customProps: {
          event_layer: 'popup',
          popup_type: POPUP_TYPE_MAP[type],
        },
      });
      if (type === 'parent') {
        dispatch(
          actions.fetchAcceptTerms({
            accepted: getAccepted(),
            version: termsList[0].version,
            verificationSessionId: apiParamsAuthResult.verificationSessionId,
            isParent: true,
          })
        );
      } else {
        const returnPageInfo =
          type !== 'under14' ? getLoginReturnPageInfo() : undefined;
        startIdVerification({
          sessionId: apiParamsAuthResult.sessionId,
          accessToken: getAccessToken(),
          isParentVerification: false,
          redirectUrl: returnPageInfo && `${WEB_HOST}${returnPageInfo}`,
        });
      }
    }
  };

  const desc = modalTextObj ? (
    <IdVerificationModalDesc
      descText={modalTextObj.descText && t(modalTextObj.descText)}
      termsList={termsList}
      termsCheckedObj={termsCheckedObj}
      onChangeItem={onChangeItem}
    />
  ) : null;
  return (
    <div className="px-10">
      <Alert
        isOpen={isShow}
        title={modalTextObj?.title && t(modalTextObj.title)}
        desc={desc}
        onRequestClose={isDimmedClicked => {
          if (idVerificationModal) {
            const {type, pageInfoAfterClose} = idVerificationModal;
            sendAuthEvent({
              actionName: 'popup_close',
              customProps: {
                event_layer: isDimmedClicked ? 'dimmed' : 'popup',
                popup_type: POPUP_TYPE_MAP[type],
              },
            });
            setTermsCheckedObj({});
            apiParamsAuthResult.verificationSessionId &&
              dispatch(
                actions.fetchDeleteUnverifiedUserData(
                  apiParamsAuthResult.verificationSessionId
                )
              );
            dispatch(actions.setValue('idVerificationModal', undefined));
            pageInfoAfterClose && routeTo(pageInfoAfterClose);
          }
        }}
        labelConfirm={t(modalTextObj?.labelConfirm || '')}
        buttonSet={ButtonSet.CancelAndConfirm}
        callback={onClickConfirm}
        confirmDisabled={!isConfirmEnabled}
        isTextAlignCenter={false}
        labelClose={t('common_close')}
      />
    </div>
  );
}

// TODO: 각 상세 페이지가 다르다면, 추후 pageInfo 변경해야 함.
const MODAL_TEXT_MAP: {
  [key in IIdVerificationModalType]: (isInSerivice?: boolean) => {
    title: string;
    descText?: string;
    agreementText?: string;
    labelConfirm: string;
    pageInfo: PageInfo;
  };
} = {
  under14: () => ({
    title: 'login_korea_signup_under_14_popup',
    agreementText: 'authentication_korea_consent_button',
    labelConfirm: 'authentication_korea_continue_button',
    pageInfo: {page: Page.MorePersonalInformationShort, type: 'auth'},
  }),
  parent: isInSerivice => ({
    title: isInSerivice
      ? 'login_korea_under_14_guardian_privacy_popup'
      : 'login_korea_signup_under_14_guardian_popup',
    labelConfirm: 'authentication_korea_continue_button',
    pageInfo: {page: Page.MorePolicyLimit14},
  }),
  limitCash: () => ({
    title: 'authentication_korea_purchase_limit_popup_title',
    descText: 'authentication_korea_purchase_limit_popup_content',
    agreementText: 'authentication_korea_consent_button',
    labelConfirm: 'authentication_korea_continue_button',
    pageInfo: {page: Page.MorePersonalInformationShort, type: 'auth'},
  }),
  adult: () => ({
    title: 'authentication_korea_view_ep_popup_title',
    descText: 'authentication_korea_view_ep_popup_content',
    agreementText: 'authentication_korea_consent_button',
    labelConfirm: 'authentication_korea_continue_button',
    pageInfo: {page: Page.MorePersonalInformationShort, type: 'auth'},
  }),
};

const POPUP_TYPE_MAP: {[key in IIdVerificationModalType]: string} = {
  under14: 'signup_agreement_under_14',
  parent: 'signup_agreement_14',
  limitCash: 'cash_identification',
  adult: 'content_identification',
};
